/* eslint-disable max-lines */
import { useSuspenseQuery } from '@apollo/client'
import type {
  ResourceUserWithGroup,
  ResourceUserWithGroupFieldsFragment,
  ViewFieldsFragment,
} from '@salescore/client-api'
import { FetchResourceUsersWithGroupDocument } from '@salescore/client-api'
import { getOrganizationIdFromPath } from '@salescore/client-common'
import {
  type DimensionFilterPickerOption,
  selectDimensionFilterOptions,
  updateDimensionFilterOptions,
  useAppDispatch,
  useAppSelector,
} from '@salescore/client-redux'
import {
  CORE_CONSTANT,
  getRecordNodesByPath,
  type ViewConfigDimensionFilter,
  type ViewConfigFilter,
  type ViewConfigKpiParameter,
  type ViewQueryField,
} from '@salescore/core'
import { useScroll } from '@salescore/frontend-common'
import { Button, Checkbox, Divider, Empty, Input, message, Space } from 'antd'
import { Array, Order, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import {
  type Dispatch,
  Fragment,
  type ReactElement,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useRecoilState } from 'recoil'
import { z } from 'zod'

import { kpiParameterAtom } from '../../../recoil/navigation/atoms'
import { useRecordsImpl } from '../../../recoil/records'
import { getLabeledFieldName } from '../../../state/util'

const MAX_FILTER_COUNT = 100

export const DimensionFilterPicker = ({
  field,
  hide,
  view,
}: {
  hide: () => void
  field: ViewQueryField
  view: ViewFieldsFragment
}): ReactElement => {
  const [kpiPivotParameter, setKpiPivotParameter] = useRecoilState(kpiParameterAtom)
  // fieldからdimensionを取得する
  // ややこしいが、KPIダッシュボード（＝ピボットテーブル）もシートと同じロジックをベースとしており、
  // すなわちRSheetを使っており、このコンポーネントに渡されるのはfield(ViewQueryField)の情報である
  // field.nameからdimensionを推測する
  const dimension = getDimension(field, kpiPivotParameter.pivot)
  const dimensionKey = [dimension?.key, dimension?.dateSpan].filter(isNotNullish).join('_')
  const isUserOrUserGroupDimension = isUserOrUserGroupDimensionKey(dimensionKey)
  const dimensionFilterOptionsCache = useAppSelector((state) =>
    selectDimensionFilterOptions(state, view.id, dimensionKey),
  )
  const optionsInCurrentRecords = useOptionsInCurrentRecords(field)
  const {
    data: { resourceUsersWithGroup },
  } = useSuspenseQuery(FetchResourceUsersWithGroupDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
    },
  })
  const dispatch = useAppDispatch()
  useEffect(() => {
    if (!isUserOrUserGroupDimension) {
      dispatch(
        updateDimensionFilterOptions({
          viewId: view.id,
          dimensionKey,
          optionsInCurrentRecords,
        }),
      )
    }
  }, [view.id, dimensionKey, optionsInCurrentRecords, dispatch, isUserOrUserGroupDimension])

  const options = isUserOrUserGroupDimension
    ? calcUserAndUserGroupOptions(dimensionKey, resourceUsersWithGroup, kpiPivotParameter, optionsInCurrentRecords)
    : dimensionFilterOptionsCache

  if (dimension === undefined) {
    // ありえないはず
    return <>error</>
  }

  const currentLeaf = kpiPivotParameter.dimensionFilterLeafs?.find(
    (leaf) => leaf.type === 'dimension' && leaf.dimension.key === dimension.key,
  )
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,@typescript-eslint/no-unnecessary-condition
  const selectedKeys = (currentLeaf?.filterValue as string[]) ?? []

  return (
    <FilterDropdown
      options={options}
      selectedKeys={selectedKeys}
      setSelectedKeys={(options) => {
        if (options.length > MAX_FILTER_COUNT) {
          // 10個以上選択することはできない
          void message.warning(t(`${MAX_FILTER_COUNT}個以上の選択はできません。`))
          return
        }
        // additionalConfigとして保存
        const leaf: ViewConfigFilter = {
          type: 'dimension' as const,
          dimension,
          filterValue: options.map((x) => x.value),
          filterValueLabel: options.map((x) => x.label).join(','),
          filterType: 'in',
        }
        setKpiPivotParameter((oldValue) => ({
          ...oldValue,
          dimensionFilterLeafs: [
            ...(oldValue.dimensionFilterLeafs?.filter(
              (x) => x.type !== 'dimension' || x.dimension.key !== dimension.key,
            ) ?? []),
            options.length > 0 ? leaf : undefined,
          ].filter(isNotNullish),
        }))
      }}
      confirm={() => {
        hide()
      }}
      clearFilters={() => {
        setKpiPivotParameter((oldValue) => ({
          ...oldValue,
          dimensionFilterLeafs:
            oldValue.dimensionFilterLeafs?.filter((x) => x.type !== 'dimension' || x.dimension.key !== dimension.key) ??
            [],
        }))
        hide()
      }}
    />
  )
}

function useOptionsInCurrentRecords(field: ViewQueryField): DimensionFilterPickerOption[] {
  const records = useRecordsImpl()
  const result = useMemo(() => {
    const targetRecords = records.flatMap((record) => getRecordNodesByPath(record, field.nodePath))
    const optionsInCurrentRecords: DimensionFilterPickerOption[] = targetRecords
      .map((record) => {
        const value = record.attributes[field.name]
        if (
          !(
            value !== undefined &&
            value !== null &&
            (typeof value === 'string' ? value !== '' : !Array.isArray(value) || value.length > 0)
          ) ||
          typeof value !== 'string' ||
          value === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING
        ) {
          // eslint-disable-next-line array-callback-return
          return
        }
        const label = record.attributes[getLabeledFieldName(field.name)] ?? value
        return {
          value,
          label: typeof label === 'string' ? label : JSON.stringify(label),
        }
      })
      .filter(isNotNullish)
    return optionsInCurrentRecords
  }, [records, field])

  return result
}

const calcUserAndUserGroupOptions = (
  dimensionKey: string,
  users: ResourceUserWithGroup[],
  kpiPivotParameter: ViewConfigKpiParameter,
  optionsInCurrentRecords: DimensionFilterPickerOption[],
) => {
  const filters = (kpiPivotParameter.dimensionFilterLeafs ?? [])
    .map((x) => (x.type === 'dimension' ? x : undefined))
    .filter(isNotNullish)
  const options = generateUserOrUserGroupOptions(users, dimensionKey, filters)
  const optionValuesInCurrentRecords = new Set(
    optionsInCurrentRecords.map((x) => x.value ?? CORE_CONSTANT.KPI_PIVOT_NULL_STRING),
  )
  return pipe(
    options,
    Array.map((x) => ({
      ...x,
      includedInCurrentRecordValues: optionValuesInCurrentRecords.has(x.value ?? CORE_CONSTANT.KPI_PIVOT_NULL_STRING),
    })),
    Array.sortBy(
      Order.combine(
        Order.mapInput(Order.Number, (x) => (x.includedInCurrentRecordValues ? 0 : 1)),
        Order.mapInput(Order.Number, (x) => (x.maybeChecked ? 0 : 1)),
      ),
    ),
  )
}

function getDimension(field: ViewQueryField, pivot: ViewConfigKpiParameter['pivot']) {
  const rowIndex = Number(field.name.replace(CORE_CONSTANT.DIMENSION_FIELD_NAME_PREFIX, '')) - 1 // XXX: このように文字列に依存する実装は見通しが悪く、また破綻しそうなのでやめたい
  const row = pivot.rows[rowIndex]
  return row
}

interface FilterDropdownProperties {
  options: DimensionFilterPickerOption[]
  selectedKeys: string[]
  setSelectedKeys: (options: DimensionFilterPickerOption[]) => void
  confirm: () => void
  clearFilters: () => void
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function FilterDropdown({ options, selectedKeys, setSelectedKeys, confirm, clearFilters }: FilterDropdownProperties) {
  const [pickedOptions, setPickedOptions] = useState(
    selectedKeys.map((key) => options.find((option) => option.value === key)).filter(isNotNullish),
  )
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const filteredOptions =
    searchKeyword !== null && searchKeyword !== ''
      ? options.filter((option) => option.label.includes(searchKeyword))
      : options
  const rootReference = useRef<HTMLDivElement>(null)

  if (options.length === 0) {
    return (
      <Empty
        description={
          <div>
            {t(`選択肢がありません。`)}
            <br />
            <Button
              className="my-3"
              onClick={() => {
                clearFilters()
              }}
            >
              {t(`クリア`)}
            </Button>
          </div>
        }
      />
    )
  }

  return (
    <div
      className="p-2"
      style={{
        maxWidth: 400,
        overflowX: 'auto',
      }}
    >
      <div className="py-1">
        <Input.Search
          placeholder={t(`検索`)}
          onKeyDown={(e) => {
            e.stopPropagation()
          }}
          onSearch={(keyword) => {
            setSearchKeyword(keyword)
            setPage(1)
          }}
        />
      </div>
      <Divider style={{ marginTop: 6, marginBottom: 6 }} />
      {pickedOptions.length === options.length ? (
        <Checkbox
          onChange={() => {
            setPickedOptions([])
          }}
          checked
        >
          {' '}
          {t(`全て解除`)}{' '}
        </Checkbox>
      ) : (
        <Checkbox
          onChange={() => {
            setPickedOptions(options)
          }}
          checked={false}
        >
          {' '}
          {t(`全て選択`)}{' '}
        </Checkbox>
      )}
      <Divider style={{ marginTop: 6, marginBottom: 6 }} />
      <div
        ref={rootReference}
        style={{
          maxHeight: 254,
          overflow: 'auto',
        }}
      >
        <VirtualizedList
          rootReference={rootReference}
          items={filteredOptions}
          page={page}
          setPage={setPage}
          render={(option) => (
            <>
              <Checkbox
                onChange={(value) => {
                  if (value.target.checked) {
                    setPickedOptions(Array.dedupeWith([...pickedOptions, option], (a, b) => a.value === b.value))
                  } else {
                    setPickedOptions([...pickedOptions].filter((x) => x.value !== option.value))
                  }
                }}
                checked={pickedOptions.map((x) => x.value).includes(option.value)}
                style={{
                  color: option.includedInCurrentRecordValues === false ? '#999' : undefined,
                }}
              >
                {option.label}
              </Checkbox>
              <br />
            </>
          )}
        />
      </div>
      <hr className="m-0 my-1 opacity-20" />
      <Space className="mt-2 flex justify-end">
        <Button
          onClick={() => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
            if (clearFilters !== undefined) {
              clearFilters()
            }
            setPickedOptions([])
            confirm()
          }}
          type="text"
          size="small"
        >
          {t(`リセット`)}
        </Button>
        <Button
          onClick={() => {
            setSelectedKeys(pickedOptions)
            confirm()
          }}
          type="primary"
          size="small"
        >
          {t(`適用`)}
        </Button>
      </Space>
    </div>
  )
}

const USER_OR_USER_GROUP_RELATED_DIMENSION_KEYS = [
  `p_salescore_user_groups_d1_id`,
  `p_salescore_user_groups_d2_id`,
  `p_salescore_user_groups_d3_id`,
  `p_salescore_user_groups_d4_id`,
  `p_salescore_user_groups_d5_id`,
  `salescore_users_id`,
]
const dimensionKeyToUserWithGroupPropertyName: Record<string, keyof ResourceUserWithGroupFieldsFragment> = {
  p_salescore_user_groups_d1_id: 'user_group_d1_id',
  p_salescore_user_groups_d2_id: 'user_group_d2_id',
  p_salescore_user_groups_d3_id: 'user_group_d3_id',
  p_salescore_user_groups_d4_id: 'user_group_d4_id',
  p_salescore_user_groups_d5_id: 'user_group_d5_id',
  salescore_users_id: 'id', // これは使われないはずだが一応
}

const dimensionKeyToUserWithGroupLabelPropertyName: Record<string, keyof ResourceUserWithGroupFieldsFragment> = {
  p_salescore_user_groups_d1_id: 'user_group_d1_name',
  p_salescore_user_groups_d2_id: 'user_group_d2_name',
  p_salescore_user_groups_d3_id: 'user_group_d3_name',
  p_salescore_user_groups_d4_id: 'user_group_d4_name',
  p_salescore_user_groups_d5_id: 'user_group_d5_name',
  salescore_users_id: 'name', // これは使われないはずだが一応
}

function isUserOrUserGroupDimensionKey(key: string) {
  return USER_OR_USER_GROUP_RELATED_DIMENSION_KEYS.includes(key)
}

function generateUserOrUserGroupOptions(
  users: ResourceUserWithGroup[],
  dimensionKey: string,
  filters: ViewConfigDimensionFilter[],
): Array<DimensionFilterPickerOption & { maybeChecked: boolean }> {
  if (!isUserOrUserGroupDimensionKey(dimensionKey)) {
    return []
  }
  const keyIndex = USER_OR_USER_GROUP_RELATED_DIMENSION_KEYS.indexOf(dimensionKey)
  if (keyIndex === -1) {
    return []
  }

  // 今対象としているdimensionのユーザー/グループよりも、上の階層のグループのフィルタを取得する
  // （例）「チーム」を対象としているときは、「部署」のフィルタを取得する。「ユーザー」「リードソース」のフィルタは無視する
  const targetFilters = filters.filter((x) => {
    if (!isUserOrUserGroupDimensionKey(x.dimension.key)) {
      return false
    }
    // 一応フィルタの中身もチェックしておく。ここでひっかかることはないはず
    if (!Array.isArray(x.filterValue) || x.filterType !== 'in') {
      return false
    }
    const filterDimensionKeyIndex = USER_OR_USER_GROUP_RELATED_DIMENSION_KEYS.indexOf(x.dimension.key)
    return filterDimensionKeyIndex < keyIndex
  })
  // フィルタを適用する
  const filteredUsers = users.filter((user) =>
    targetFilters.every((filter) => {
      const ids = z.string().array().safeParse(filter.filterValue)
      if (!ids.success) {
        return true
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const property = dimensionKeyToUserWithGroupPropertyName[filter.dimension.key]!
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const id = user[property]!
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return ids.data.includes(id as string)
    }),
  )
  // 選択肢として返す
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const idProperty = dimensionKeyToUserWithGroupPropertyName[dimensionKey]!
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const labelProperty = dimensionKeyToUserWithGroupLabelPropertyName[dimensionKey]!
  const currentDimensionFilterValues = new Set(
    filters
      .filter((x) => x.dimension.key === dimensionKey)
      .flatMap((x) => {
        const validated = z.string().array().safeParse(x.filterValue)
        return validated.success ? validated.data : []
      }),
  )

  return pipe(
    filteredUsers,
    Array.map((user) => {
      if (dimensionKey === `salescore_users_id`) {
        // 以下相当をバックエンド側で出しておきたいが、現状できていないのでここでやる
        const groupNames = [
          user.user_group_d1_name,
          user.user_group_d2_name,
          user.user_group_d3_name,
          user.user_group_d4_name,
          user.user_group_d5_name,
        ].filter(isNotNullish)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const groupNameLabel = groupNames.length > 0 ? `(${groupNames.at(-1)!})` : ''
        return {
          value: user.id,
          label: [user.name, groupNameLabel].join(' '),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          maybeChecked: currentDimensionFilterValues.has(user[idProperty] as string),
        }
      }
      const value = user[idProperty]
      if (value === undefined || value === null) {
        return {
          value: null,
          label: '所属なし',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          maybeChecked: currentDimensionFilterValues.has(user[idProperty] as string),
        }
      }
      if (typeof value !== 'string') {
        // eslint-disable-next-line array-callback-return
        return
      }
      return {
        value,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        label: user[labelProperty] as string,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        maybeChecked: currentDimensionFilterValues.has(user[idProperty] as string),
      }
    }),
    Array.filter(isNotNullish),
    Array.dedupeWith((a, b) => a.value === b.value),
  )
}

const PAGE_SIZE = 50
const SCROLL_OFFSET_PX = 50

// eslint-disable-next-line sonarjs/prefer-read-only-props
function VirtualizedList({
  rootReference,
  items,
  page,
  setPage,
  render,
}: {
  rootReference: React.RefObject<HTMLDivElement>
  items: DimensionFilterPickerOption[]
  page: number
  setPage: Dispatch<SetStateAction<number>>
  render: (item: DimensionFilterPickerOption) => React.ReactNode
}) {
  const renderingItemCount = page * PAGE_SIZE

  useScroll(rootReference, (currentScrollPosition) => {
    const root = rootReference.current
    if (root === null || items.length < renderingItemCount) return

    // 一番下までスクロールした際のスクロール位置
    const scrollEndPosition = root.scrollHeight - root.clientHeight
    if (currentScrollPosition.y > scrollEndPosition - SCROLL_OFFSET_PX) setPage((x) => x + 1)
  })

  return (
    <>
      {items.slice(0, renderingItemCount).map((item, index) => (
        <Fragment key={`${item.value}-${index}`}>{render(item)}</Fragment>
      ))}
    </>
  )
}
